import { useEffect, useRef, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { api } from "@/api/axios";

interface QrDialogProps {
  configId: number;
  title: string;
  open: boolean;
  onClose: () => void;
}

export function QrDialog({ configId, title, open, onClose }: QrDialogProps) {
  const [svgUrl, setSvgUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  const loading = open && !svgUrl && !error;

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    api
      .get(`/api/config/wg/${configId}/qr`)
      .then((res) => {
        if (cancelled) return;
        try {
          const blob = new Blob([res.data.svg], {
            type: "image/svg+xml;charset=utf-8",
          });
          const objectUrl = URL.createObjectURL(blob);
          if (urlRef.current) URL.revokeObjectURL(urlRef.current);
          urlRef.current = objectUrl;
          setSvgUrl(objectUrl);
          setError(null);
        } catch {
          setError("Не удалось создать QR-код");
        }
      })
      .catch(() => {
        if (!cancelled) setError("Не удалось загрузить QR-код");
      });

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [open, configId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-slate-900 border border-slate-700 rounded-xl p-5 max-w-sm w-full mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <h3 className="text-sm font-medium text-slate-300 mb-4 text-center pr-6">
          {title}
        </h3>

        {loading && (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
          </div>
        )}

        {error && !loading && (
          <div className="text-center text-red-400 text-sm py-8">{error}</div>
        )}

        {svgUrl && !loading && (
          <div className="flex justify-center">
            <img
              src={svgUrl}
              alt="QR Code"
              className="bg-white rounded-lg p-3"
            />
          </div>
        )}

        <p className="text-xs text-slate-500 text-center mt-3">
          Отсканируйте в приложении для подключения
        </p>
      </div>
    </div>
  );
}
